import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  Flex,
  Grid,
  Page,
  Heading,
  Row,
  Column,
  Spacer,
  Button,
  Divider,
  Text,
  Icon,
} from '@oliasoft-open-source/react-ui-library';
import { round } from '@oliasoft-open-source/units';
import { navigateToPath } from 'store/navigation/navigation';
import { formatToMillions } from 'client/common/format-data/format-to-millions';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import { ProjectSettingsModal } from 'src/views/settings/project-settings/project-settings.modal';
import { getSimulations, simulationCleanup } from 'store/entities/simulations/simulations';
import { getTimeTrackerItems, listCleared as timeTrackerCleanUp } from 'store/entities/time-tracker/time-tracker';
import { getCostTrackerItems, listCleared as costTrackerCleanUp } from 'store/entities/cost-tracker/cost-tracker';
import { selectSimulationsResult } from 'store/entities/simulations/selectors';
import { selectTimeTrackerFetching, selectActualTime } from 'store/entities/time-tracker/selectors';
import { selectActualCost } from 'store/entities/cost-tracker/selectors';
import { selectActivityModelLoaders } from 'store/entities/activity-model/selectors';
import { getProjectSettings, showProjectSettingsModalUpdated } from 'store/entities/project-settings/project-settings';
import { selectProjectOverview } from 'store/entities/projects/selectors';
import { selectContributors } from 'store/entities/audit-log/selectors';
import translations from 'src/internationalisation/translation-map.json';
import { ResultChartGrid } from 'src/views/simulations/result-overview/result-chart-grid';
import { Report } from 'src/views/report/report';
import { chartKeys, getPercentiles, selectOptions } from 'src/views/simulations/result-overview/result-utils';
import { formatFromApiDate } from 'src/views/time-tracker/utils/date/dateUtils';
import { AuditLog } from 'views/audit-log/audit-log';
import { updateNumberOfCharts, dataTypeUpdated } from 'src/store/entities/ui/simulations/chart-controls';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { isEmpty } from 'lodash';
import { getCostCodesItems } from 'src/store/entities/cost-codes/cost-codes';
import { downloadAFEReportExcel } from 'src/views/overview/utils/download-afe-report-excel';
import { selectCostCodesIdToCodeMap } from 'src/store/entities/cost-codes/selectors';
import { selectPrimaryCurrency } from 'src/store/entities/company-settings/selectors';
import { PageCard } from './page-card';
import { OverviewDrawer } from './drawer/overview.drawer';
import './style.css';

const Overview = ({
  projectOverview,
  showProjectSettingsModal,
  showProjectSettingsModalUpdated,
  getProjectSettings,
  updateNumberOfCharts,
  dataTypeUpdated,
  simulationsResult,
  lastTimeTrackerOperation,
  startDate,
  isTimeTrackerFetching,
  isOperationsFetching,
  actualTime,
  actualCost,
  contributors,
  getSimulations,
  simulationCleanup,
  getTimeTrackerItems,
  timeTrackerCleanUp,
  getCostTrackerItems,
  costTrackerCleanUp,
  getCostCodesItems,
  costCodeIdToCodeMap,
  primaryCurrency,
  isPageDisabled,
}) => {
  const { t } = useTranslation();
  const { project } = useParams();
  const { name, wellbore } = projectOverview;

  useEffect(() => {
    getProjectSettings(project);
    updateNumberOfCharts(2);
    dataTypeUpdated({ key: chartKeys[0], values: selectOptions[8] });
    dataTypeUpdated({ key: chartKeys[1], values: selectOptions[0] });
  }, []);

  useEffect(() => {
    if (project) {
      getSimulations(project);
      getTimeTrackerItems(project);
      getCostTrackerItems(project);
      getCostCodesItems();
    }
    return () => {
      simulationCleanup();
      timeTrackerCleanUp();
      costTrackerCleanUp();
    };
  }, [project]);

  const totalTimeMain = round(simulationsResult?.average / 24, 1);
  const totalTimeSeconary = () => {
    const time10 = round(getPercentiles(simulationsResult, 10, 'time') / 24, 1);
    const time90 = round(getPercentiles(simulationsResult, 90, 'time') / 24, 1);
    if ((time10 && time90) || time10 === 0 || time90 === 0) {
      return `${time10} - ${time90} ${t(translations.days).toLocaleLowerCase()}`;
    } else {
      return '';
    }
  };

  const totalCostMain = formatToMillions(simulationsResult?.average_cost);
  const totalCostSecondary = () => {
    const cost10 = formatToMillions(getPercentiles(simulationsResult, 10, 'cost'));
    const cost90 = formatToMillions(getPercentiles(simulationsResult, 90, 'cost'));
    if ((cost10 && cost90) || cost10 === 0 || cost90 === 0) {
      return `${cost10} m - ${cost90} m ${primaryCurrency}`;
    } else {
      return '';
    }
  };

  const riskContribution = simulationsResult?.average_combined_risk_time
    ? round(simulationsResult?.average_combined_risk_time / 24, 1)
    : 0;

  const reportRef = useRef(null);

  // Call the child's exposed function
  const callGenerateReport = () => {
    if (reportRef.current) {
      reportRef.current.generateReport();
    }
  };

  // Call the child's exposed function
  const callGenerateAfeReport = () => {
    if (reportRef.current) {
      reportRef.current.generateAfeReport();
    }
  };
  const summaryData = {
    estimatedTime: totalTimeMain,
    actualTime,
    estimatedCost: totalCostMain,
    actualCost: formatToMillions(actualCost),
    risk: riskContribution,
    contributors,
  };
  return (
    <>
      <Page padding={0} scroll={false}>
        <Row spacing={0} height="100%">
          <OverviewDrawer />
          <Column scroll>
            <div className="overview-wrapper">
              <div className="overview-header">
                <Flex justifyContent="space-between" gap>
                  <Heading top marginBottom={0} testId="overview-title">
                    {name} ({wellbore?.name || t(translations.wellbore)})
                  </Heading>
                  <Flex gap>
                    <Button
                      name="project-settings"
                      label={t(translations.projects_projectSettings)}
                      testId={`overview-button-${convertToKebabCase(t(translations.projects_projectSettings))}`}
                      onClick={() => showProjectSettingsModalUpdated(true)}
                      disabled={isPageDisabled}
                    />
                    <Button
                      name="download-estimate-summary"
                      label={
                        <Text>
                          {t(translations.projects_estimateSummary)} <Icon icon="download" />
                        </Text>
                      }
                      testId={`overview-button-${convertToKebabCase(t(translations.projects_estimateSummary))}`}
                      onClick={() => callGenerateReport()}
                      disabled={isPageDisabled}
                    />
                    <Button
                      name="download-afe-estimate-summary"
                      label={
                        <Text>
                          {t(translations.projects_afeReportPdf)} <Icon icon="download" />
                        </Text>
                      }
                      testId={`overview-button-${convertToKebabCase(t(translations.projects_afeReportPdf))}`}
                      onClick={() => callGenerateAfeReport()}
                      disabled={isPageDisabled}
                    />
                    <Button
                      name="download-afe-report"
                      label={
                        <Text>
                          {t(translations.projects_afeReportExcel)} <Icon icon="download" />
                        </Text>
                      }
                      testId={`overview-button-${convertToKebabCase(t(translations.projects_afeReportExcel))}`}
                      onClick={() =>
                        downloadAFEReportExcel(
                          simulationsResult,
                          costCodeIdToCodeMap,
                          primaryCurrency,
                          name,
                          wellbore?.name,
                        )
                      }
                      disabled={isEmpty(simulationsResult) || isPageDisabled}
                    />
                  </Flex>
                </Flex>
                <Spacer />
                <Divider margin={0} />
                <Grid columns="repeat(4, 1fr)" columnsTablet="repeat(3, 1fr)" gap justifyContent="space-between">
                  <PageCard
                    testId="overview"
                    title={t(translations.simulations_totalTime)}
                    mainValue={
                      totalTimeMain || totalTimeMain === 0
                        ? `${totalTimeMain} ${t(translations.days).toLocaleLowerCase()}`
                        : ''
                    }
                    secondaryValue={totalTimeSeconary()}
                  />
                  <PageCard
                    testId="overview"
                    title={t(translations.costSetup_totalCost)}
                    mainValue={totalCostMain || totalCostMain === 0 ? `${totalCostMain} m ${primaryCurrency}` : ''}
                    secondaryValue={totalCostSecondary()}
                  />
                  <PageCard
                    title={t(translations.estimatedFinish)}
                    mainValue={
                      simulationsResult?.operations && !lastTimeTrackerOperation?.finish
                        ? t(translations.notFound)
                        : formatFromApiDate(lastTimeTrackerOperation?.finish)
                    }
                    secondaryValue={''}
                    isLoading={isTimeTrackerFetching}
                  />
                  <PageCard
                    title={t(translations.projects_riskContribution)}
                    mainValue={
                      riskContribution || riskContribution === 0
                        ? `${riskContribution} ${t(translations.days).toLocaleLowerCase()}`
                        : ''
                    }
                    secondaryValue={''}
                    isLoading={isOperationsFetching}
                    tooltip={t(translations.projects_toolTipRiskContribution)}
                  />
                </Grid>
              </div>
              <Report
                reportRef={reportRef}
                simulationsResult={simulationsResult}
                projectOverview={{ ...projectOverview, startDate }}
                summaryData={summaryData}
              />
              <ResultChartGrid isOverview />
              <Divider margin={0} />
              <div className="overview-audit-log">
                <AuditLog />
              </div>
            </div>
          </Column>
        </Row>
      </Page>
      {showProjectSettingsModal && <ProjectSettingsModal setModalVisible={showProjectSettingsModalUpdated} />}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const { isFetching: isOperationsFetching } = selectActivityModelLoaders({
    entities,
  });
  return {
    operations: entities.activityModel.operations,
    projectOverview: selectProjectOverview(entities),
    showProjectSettingsModal: entities.projectSettings.showProjectSettingsModal,
    simulationsResult: selectSimulationsResult(entities),
    isTimeTrackerFetching: selectTimeTrackerFetching(entities),
    lastTimeTrackerOperation: entities?.timeTracker?.list?.slice(-1)[0],
    startDate: entities?.timeTracker?.list?.[0]?.start,
    isOperationsFetching,
    actualTime: selectActualTime(entities),
    actualCost: selectActualCost(entities),
    contributors: selectContributors(entities),
    costCodeIdToCodeMap: selectCostCodesIdToCodeMap({ entities }),
    primaryCurrency: selectPrimaryCurrency(entities),
  };
};

const mapDispatchToProps = {
  navigateToPath,
  showProjectSettingsModalUpdated,
  getProjectSettings,
  updateNumberOfCharts,
  getSimulations,
  simulationCleanup,
  getTimeTrackerItems,
  timeTrackerCleanUp,
  getCostTrackerItems,
  costTrackerCleanUp,
  getCostCodesItems,
  dataTypeUpdated,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(Overview), { isPage: true });

export { Container as Overview };
